import { Avatar, AvatarFallback } from '@heidi/design-system/avatar';
import { Chip } from '@heidi/design-system/chip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@heidi/design-system/dropdown-menu';
import { SearchInput } from '@heidi/design-system/search-input';
import { TypographyP2 } from '@heidi/design-system/typography';
import { AssigneeType } from '@heidi/schema/ml-scribe';
import { cn } from '@heidi/utils';
import {
  useQueryOrganizationMembersSearch,
  useQueryOrganizationTeams,
  useQueryUser,
} from '@heidi/workspace/data';
import { Check, UserRound, UserRoundX } from 'lucide-react';
import { useMemo, useRef, useState, type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { useDebounce } from 'use-debounce';
import { ClinicalTaskAssigneeEmptyStatePrompt } from './clinical-task-assignee-empty-state-prompt';
import { AssigneeCurrentItem } from './clinical-task-assignee-list-items';
import { ClinicalTaskAssigneeResultsList } from './clinical-task-assignee-results-list';
import { ClinicalTaskAssigneeTriggerIcon } from './clinical-task-assignee-trigger';
import { useDisplayAssignee } from './use-display-assignee';

interface ClinicalTaskAssigneeSelectorProps {
  assigneeId?: string | null;
  assigneeName?: string | null;
  assigneeAvatarUrl?: string | null;
  assigneeType?: AssigneeType;
  /**
   * Whether this surface may assign a team (vs. only display an existing
   * team assignee made elsewhere). Required so every consumer makes an
   * explicit choice instead of inheriting the org-wide flag by accident.
   */
  enableTeams: boolean;
  onValueChange: (
    assigneeId: string | null,
    assigneeName: string | null,
    assigneeAvatarUrl: string | null,
    assigneeType?: AssigneeType
  ) => void;
  /** Pairs the Heidi mark on the owner trigger. Heidi is not listed in the picker. */
  delegatedToHeidi?: boolean;
  compact?: boolean;
  emptyIcon?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  'aria-label'?: string;
  disabled?: boolean;
  align?: 'start' | 'center' | 'end';
}

export function ClinicalTaskAssigneeSelector({
  assigneeId,
  assigneeName,
  assigneeAvatarUrl,
  assigneeType,
  enableTeams,
  onValueChange,
  delegatedToHeidi = false,
  compact = false,
  emptyIcon = (
    <Avatar size="xs" color="empty" className="shrink-0">
      <AvatarFallback icon={<UserRound />} />
    </Avatar>
  ),
  open: openProp,
  onOpenChange,
  className,
  'aria-label': ariaLabel,
  disabled = false,
  align = 'center',
}: ClinicalTaskAssigneeSelectorProps) {
  const intl = useIntl();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : uncontrolledOpen;
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const displayAssignee = useDisplayAssignee({
    open,
    assigneeId,
    assigneeName,
    assigneeAvatarUrl,
    assigneeType,
  });

  const { data: currentUserId } = useQueryUser((data) => data?.kinde_user_id);
  const { data: teamsData } = useQueryOrganizationTeams(enableTeams && open);
  const teams = teamsData?.teams ?? [];
  const [debouncedSearch] = useDebounce(search, 300);
  const filteredTeams = debouncedSearch
    ? teams.filter((t) => t.name.toLowerCase().includes(debouncedSearch.toLowerCase()))
    : teams;
  const { data, isLoading } = useQueryOrganizationMembersSearch(
    debouncedSearch,
    open && debouncedSearch.length > 0
  );
  const members = useMemo(() => {
    // Filtered here, once, so every consumer downstream (the results list AND
    // `showNoResults` below) sees only rows a real assignee can resolve to —
    // a member with no `kinde_user_id` can't be selected or rendered.
    return [...(data?.members ?? [])]
      .filter(
        (member): member is typeof member & { kinde_user_id: string } =>
          !!member.kinde_user_id
      )
      .sort((a, b) => {
        if (a.kinde_user_id === currentUserId) return -1;
        if (b.kinde_user_id === currentUserId) return 1;
        if (a.kinde_user_id === assigneeId) return -1;
        if (b.kinde_user_id === assigneeId) return 1;
        return 0;
      });
  }, [data?.members, currentUserId, assigneeId]);

  const hasQuery = search.length > 0;
  const isResultsPending = hasQuery && (isLoading || search !== debouncedSearch);
  const showNoResults =
    hasQuery && !isResultsPending && members.length === 0 && filteredTeams.length === 0;

  const handleOpenChange = (nextOpen: boolean) => {
    if (!isControlled) setUncontrolledOpen(nextOpen);
    onOpenChange?.(nextOpen);
    if (!nextOpen) {
      setSearch('');
    } else {
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  };

  const handleSelect = (
    userId: string,
    name: string | null,
    avatarUrl: string | null
  ) => {
    onValueChange(userId, name, avatarUrl);
    handleOpenChange(false);
  };

  // Clears the owner slot only. Heidi is a separate axis and is not listed in
  // this picker, so dropping the owner leaves a Work run in place.
  const handleClear = () => {
    if (assigneeId) onValueChange(null, null, null);
    handleOpenChange(false);
  };

  const handleTeamSelect = (team: { id: string; name: string }) => {
    onValueChange(team.id, team.name, null, AssigneeType.team);
    handleOpenChange(false);
  };

  const assignedLabel = intl.formatMessage({
    defaultMessage: 'Assigned',
    id: 'iZDRGORYVZ',
  });

  return (
    <DropdownMenu open={open} onOpenChange={disabled ? undefined : handleOpenChange}>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Chip
          variant={assigneeId ? 'ghost' : 'empty'}
          aria-label={ariaLabel}
          data-testid="assignee-selector"
          disabled={disabled}
          leadingIcon={
            <ClinicalTaskAssigneeTriggerIcon
              assigneeId={assigneeId}
              assigneeName={assigneeName}
              assigneeAvatarUrl={assigneeAvatarUrl}
              assigneeType={assigneeType}
              emptyIcon={emptyIcon}
              withHeidi={delegatedToHeidi}
            />
          }
          className={cn(
            'max-w-40 justify-center truncate',
            compact &&
              'overflow-visible px-0 hover:bg-transparent active:bg-transparent has-[[data-slot=chip-leading-icon]]:ps-0 data-[state=open]:bg-transparent',
            className
          )}
        >
          {!compact &&
            (assigneeId ? (
              <span className="min-w-0 truncate">{assigneeName ?? assignedLabel}</span>
            ) : (
              intl.formatMessage({ defaultMessage: 'Assign', id: 'guunZtojiY' })
            ))}
        </Chip>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-60"
        align={align}
        sideOffset={5}
        collisionPadding={align === 'center' ? undefined : 8}
      >
        <SearchInput
          ref={searchRef}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key.length === 1) e.stopPropagation();
          }}
          placeholder={intl.formatMessage({
            defaultMessage: 'Assign to...',
            id: 'W0aij2p/Qq',
          })}
          clearAriaLabel={intl.formatMessage({
            defaultMessage: 'Clear search',
            id: '4YJHutR26p',
          })}
          wrapperClassName="border-none"
        />

        <DropdownMenuSeparator />

        <div className="max-h-60 overflow-y-auto">
          <DropdownMenuItem onClick={handleClear}>
            {/* mx-px pads to 18px so the label aligns with the Avatar xs rows below. */}
            <UserRoundX className="mx-px text-text-secondary" />
            <TypographyP2 className="flex-1 text-start text-text-primary">
              {intl.formatMessage({
                defaultMessage: 'No assignee',
                id: 'eFkWKWZQXO',
              })}
            </TypographyP2>
            {!assigneeId && <Check className="text-text-primary" />}
          </DropdownMenuItem>

          {!hasQuery && displayAssignee && (
            <AssigneeCurrentItem
              assigneeId={assigneeId}
              assigneeName={displayAssignee.name}
              assigneeAvatarUrl={displayAssignee.avatarUrl}
              assigneeType={displayAssignee.type}
              assignedLabel={assignedLabel}
              onSelect={() => handleOpenChange(false)}
            />
          )}

          {!hasQuery && (
            <div className="p-2">
              <ClinicalTaskAssigneeEmptyStatePrompt
                text={intl.formatMessage({
                  defaultMessage: 'Start typing to search',
                  id: 'RKRuF7Q3w9',
                })}
              />
            </div>
          )}

          <ClinicalTaskAssigneeResultsList
            hasQuery={hasQuery}
            isResultsPending={isResultsPending}
            showNoResults={showNoResults}
            enableTeams={enableTeams}
            filteredTeams={filteredTeams}
            members={members}
            assigneeId={assigneeId}
            assignedLabel={assignedLabel}
            onSelectTeam={handleTeamSelect}
            onSelectMember={handleSelect}
          />
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
